import { useTranslation } from 'react-i18next';
import { Flag, Languages } from 'lucide-react';
import { SUPPORTED_LANGUAGES, type LanguageCode } from '@/config/languages';
import { uiLocales } from '@/i18n/negotiate';
import { useLocale } from '@/i18n/useLocale';
import { cn } from '@/lib/utils';
import { COARSE_HIT_32 } from '@/lib/touch-target';
import { useChooseLanguage } from './useChooseLanguage';
import { useReportTranslation } from './useReportTranslation';

/**
 * The compact language switcher every shell carries (#2109 R1 lane L4;
 * research §2.2 rule 8): native names only, one icon, no heading. Anonymous
 * visitors' choices go to `localStorage['trf.lang']`; a signed-in choice
 * also PATCHes the profile — the same write Settings makes
 * (`useChooseLanguage`). `aria-label` is `common.language.label`, so a
 * screen reader hears "Language" in the active language. The option names
 * are each language's own name for itself — not copy — so the `<select>` is
 * `translate="no"`: browser translation must not turn "Español" into
 * "Spanish", and the pseudo-locale guard skips it for the same reason.
 *
 * #2120: beside the select, a signed-in reader of a non-English page gets a
 * flag button, "Report a translation", that opens ReportTranslationDialog.
 * It is the action's home below md, where the machine-translation banner is
 * too narrow for its own copy of it, and after the banner is dismissed.
 *
 * `compact` (the public top bar, Victor 2026-10-21): the icon alone. The
 * native `<select>` lies over it, transparent, so a click or tap opens the
 * same list of native names and the control keeps its accessible name; the
 * icon's words are on `title` for the hover (guideline §1.10).
 */
export function LanguageSwitcher({ className, compact = false }: { className?: string; compact?: boolean }) {
  const { t } = useTranslation();
  const { locale, staffEnglishOnly } = useLocale();
  const choose = useChooseLanguage();
  const offered = uiLocales();
  const options = SUPPORTED_LANGUAGES.filter((l) => offered.includes(l.code));
  const report = useReportTranslation();

  if (staffEnglishOnly) return null;

  const optionList = options.map((lang) => (
    <option key={lang.code} value={lang.code} lang={lang.bcp47}>
      {lang.nativeName}
    </option>
  ));

  return (
    // `isolate`: the report button's coarse hit area sits at -z-10 inside it,
    // under the select's drawn box (touch-target.ts BEHIND flavour).
    <span className={cn('isolate inline-flex items-center gap-1.5 text-stevens-ink-2', className)}>
      {compact ? (
        <span
          title={t('language.label')}
          className="relative inline-flex h-8 w-8 [@media(pointer:coarse)]:h-11 [@media(pointer:coarse)]:w-11 items-center justify-center rounded-md border border-transparent hover:border-stevens-rule focus-within:ring-2 focus-within:ring-ring"
        >
          <Languages className="h-4 w-4 shrink-0" aria-hidden="true" />
          <select
            translate="no"
            aria-label={t('language.label')}
            value={locale}
            onChange={(e) => void choose(e.target.value as LanguageCode)}
            className="absolute inset-0 h-full w-full cursor-pointer appearance-none opacity-0 focus:outline-none"
          >
            {optionList}
          </select>
        </span>
      ) : (
        <>
          <Languages className="h-4 w-4 shrink-0" aria-hidden="true" />
          <select
            translate="no"
            aria-label={t('language.label')}
            value={locale}
            onChange={(e) => void choose(e.target.value as LanguageCode)}
            // #2698: 32 px drawn; 44 px tall on a touch screen. A <select> takes
            // no ::after, so the box itself grows; it has no visible border until
            // hover, so only the row height changes, and only on touch.
            className="h-8 [@media(pointer:coarse)]:h-11 max-w-[10rem] cursor-pointer rounded-md border border-transparent bg-transparent py-0 pe-1 ps-1 text-body-sm text-stevens-ink-2 hover:border-stevens-rule focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {optionList}
          </select>
        </>
      )}
      {report.available && (
        <button
          type="button"
          {...report.triggerProps}
          aria-label={t('language.report.action')}
          title={t('language.report.action')}
          className={cn(
            'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-stevens-ink-2 hover:bg-[color-mix(in_oklab,currentColor_12%,transparent)] focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            COARSE_HIT_32,
          )}
        >
          <Flag className="h-4 w-4" aria-hidden="true" />
        </button>
      )}
      {report.dialog}
    </span>
  );
}
